<!-- FILE: components/Table/CustomProgressBar.vue -->
<template>
  <div
    class="v-progress-linear"
    :class="progressClasses"
    :style="containerStyle"
    role="progressbar"
    :aria-valuenow="normalizedValue"
    aria-valuemin="0"
    aria-valuemax="100"
  >
    <!-- Background layer -->
    <div class="v-progress-linear__background" :style="backgroundStyle"></div>

    <!-- Bar (filled portion) -->
    <div class="v-progress-linear__bar">
      <div class="v-progress-linear__bar__determinate" :class="barClasses" :style="barStyle"></div>
    </div>

    <!-- Content/Slot -->
    <div class="v-progress-linear__content">
      <slot></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: "CustomProgressBar",
  props: {
    // Value between 0-100
    value: {
      type: [Number, String],
      default: 0,
    },
    // Color name or hex
    color: {
      type: String,
      default: "primary",
    },
    // Height in pixels
    height: {
      type: [Number, String],
      default: 4,
    },
    // Enable background color
    backgroundColor: {
      type: String,
      default: null,
    },
    // Rounded corners
    rounded: {
      type: Boolean,
      default: false,
    },
    // Striped pattern
    striped: {
      type: Boolean,
      default: false,
    },
    // Reactive/animated
    reactive: {
      type: Boolean,
      default: false,
    },
    // Indeterminate loading
    indeterminate: {
      type: Boolean,
      default: false,
    },
    // Stream mode
    stream: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    normalizedValue() {
      const val = parseFloat(this.value);
      return Math.min(100, Math.max(0, val || 0));
    },

    progressClasses() {
      return {
        "v-progress-linear--rounded": this.rounded,
        "v-progress-linear--striped": this.striped,
        "v-progress-linear--reactive": this.reactive,
        "v-progress-linear--indeterminate": this.indeterminate,
        "v-progress-linear--stream": this.stream,
      };
    },

    containerStyle() {
      const heightValue = typeof this.height === "number" ? `${this.height}px` : this.height;
      return {
        height: heightValue,
      };
    },

    backgroundStyle() {
      // Vuetify uses lighter shade for background
      const bgColor = this.backgroundColor || this.getLightColor(this.color);
      return {
        backgroundColor: bgColor,
        opacity: 0.3,
      };
    },

    barStyle() {
      return {
        width: `${this.normalizedValue}%`,
        backgroundColor: this.getColor(this.color),
      };
    },

    barClasses() {
      return {
        "v-progress-linear__bar__determinate--striped": this.striped,
      };
    },
  },
  methods: {
    getColor(color) {
      // Vuetify color palette
      const colors = {
        primary: "#1976D2",
        secondary: "#424242",
        accent: "#82B1FF",
        error: "#FF5252",
        info: "#2196F3",
        success: "#4CAF50",
        warning: "#FB8C00",
        green: "#4CAF50",
        blue: "#2196F3",
        red: "#F44336",
        orange: "#FF9800",
        purple: "#9C27B0",
        pink: "#E91E63",
        teal: "#009688",
        cyan: "#00BCD4",
        grey: "#9E9E9E",
      };

      // Return color from palette or use as-is if hex
      return colors[color] || color;
    },

    getLightColor(color) {
      // Return lighter version for background
      const lightColors = {
        primary: "#BBDEFB",
        secondary: "#BDBDBD",
        accent: "#82B1FF",
        error: "#FFCDD2",
        info: "#BBDEFB",
        success: "#C8E6C9",
        warning: "#FFE0B2",
        green: "#C8E6C9",
        blue: "#BBDEFB",
        red: "#FFCDD2",
        orange: "#FFE0B2",
        purple: "#E1BEE7",
        pink: "#F8BBD0",
        teal: "#B2DFDB",
        cyan: "#B2EBF2",
        grey: "#F5F5F5",
      };

      return lightColors[color] || "#E0E0E0";
    },
  },
};
</script>

<style scoped>
/* Base Progress Linear */
.v-progress-linear {
  position: relative;
  overflow: hidden;
  width: 100%;
  background: transparent;
  border-radius: 0;
}

/* Rounded variant */
.v-progress-linear--rounded {
  border-radius: 4px;
}

/* Background layer */
.v-progress-linear__background {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  transition: inherit;
}

/* Bar container */
.v-progress-linear__bar {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 1;
}

/* Bar determinate */
.v-progress-linear__bar__determinate {
  height: 100%;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.6, 1);
  position: relative;
}

/* Striped pattern - Solid color stripes like your gray pattern */
.v-progress-linear__bar__determinate--striped {
  background: repeating-linear-gradient(
    -45deg,
    rgba(0, 0, 0, 0.15),
    /* Darker stripe */ rgba(0, 0, 0, 0.15) 10px,
    transparent 0,
    /* Lighter stripe (base color shows through) */ transparent 20px
  );
}
/* Content/slot */
.v-progress-linear__content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  pointer-events: none;
}

/* Indeterminate animation */
.v-progress-linear--indeterminate .v-progress-linear__bar__determinate {
  width: 100% !important;
}

/* Stream effect */
.v-progress-linear--stream .v-progress-linear__bar__determinate::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 10px;
  background: currentColor;
  opacity: 0.4;
}
</style>
